import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, Typography, Button, Flex } from "antd";
import { toastService } from "../../../../common/toast-messages/toast";
import {
  disableOnWheel,
  sanitizeDataTypeClientSide,
} from "../../../../../utils/utils";
import RequiredLabel from "../../../../common/required-label/RequiredLabel";
import { colors } from "../../../../../constants/colors";
import useLocalStorage from "../../../../../hooks/useLocalStorage";
import { setupProfile } from "../../../../../redux/services/auth/authServices";
import { appConfig } from "../../../../../constants/config/appConfig";
import styles from "./AddBankAccount.module.scss";
const { Title } = Typography;

const AddBankAccount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { profileSetUpDetails } = useSelector((state) => state?.auth);

  /* local storage dependencies */
  const { useSession, setUserSession } = useLocalStorage(
    appConfig.SESSION_USER_INFO
  );
  /* local storage dependencies */

  //   useEffect(()=>{
  //     if (profileSetUpDetails && profileSetUpDetails?.payout_details && Object.keys(profileSetUpDetails)?.length !== 0 && profileSetUpDetails !== undefined && profileSetUpDetails !== null) {

  //       form.setFieldsValue({
  //         beneficiaryName: profileSetUpDetails?.payout_details?.account_holder_name,
  //         confirmAccountNumber: profileSetUpDetails?.payout_details?.account_number,
  //         accountNumber: profileSetUpDetails?.payout_details?.account_number,
  //         bankCode: profileSetUpDetails?.payout_details?.bank_code,
  //         bankName: profileSetUpDetails?.payout_details?.bank_name,
  //         swiftCode: profileSetUpDetails?.payout_details?.swift_code,
  //         currency: profileSetUpDetails?.payout_details?.currency,
  //         routing_number: profileSetUpDetails?.payout_details?.routing_number
  //       });
  //     }
  // },[])

  const onFinish = (values) => {
    if (values.accountNumber === values?.confirmAccountNumber) {
      const { currency, ...restProfileSetupDetails } = profileSetUpDetails;
      let profileBody = {
        ...restProfileSetupDetails,
        payout_details: {
          account_holder_name: values?.beneficiaryName,
          account_number: values?.confirmAccountNumber,
          bank_code: values?.bankCode,
          bank_name: values?.bankName,
          swift_code: values?.swiftCode,
          currency,
          routing_number: values?.routing_number,
        },
        cash: false,
      };

      delete profileBody.cash;

      dispatch(setupProfile(profileBody, navigate, setLoading, setUserSession));
    } else {
      toastService.error(t("addBankAccount.accountNumberMessage"));
    }
  };

  return (
    <>
      <Flex vertical gap="1rem" className={styles.addBankAccountContainer}>
        <Title level={1} style={{ color: colors.primaryColor }}>
          {t("addBankAccount.title")}
        </Title>
        <Title level={4} type="secondary" style={{ margin: 0 }}>
          {t("addBankAccount.subTitle")}
        </Title>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          requiredMark={false}
        >
          <Flex gap="1rem" vertical>
            <div>
              <Form.Item
                label={
                  <RequiredLabel
                    text={t("addBankAccount.beneficiaryNameLabel")}
                    required={true}
                  />
                }
                name="beneficiaryName"
                rules={[
                  {
                    required: true,
                    message: t("addBankAccount.beneficiaryNameErrorMessage"),
                  },
                  {
                    whitespace: true,
                    message: t("addBankAccount.beneficiaryNameErrorMessage"),
                  },
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
              >
                <Input
                  placeholder={t(
                    "addBankAccount.beneficiaryNameLabelPlaceholder"
                  )}
                />
              </Form.Item>

              <Form.Item
                label={
                  <RequiredLabel
                    text={t("addBankAccount.accountNumberLabel")}
                    required={true}
                  />
                }
                name="accountNumber"
                rules={[
                  {
                    required: true,
                    message: t("addBankAccount.accountNumberLabelErrorMessage"),
                  },
                  {
                    whitespace: true,
                    message: t(
                      "addBankAccount.confirmAccountNumberErrorMessage"
                    ),
                  },
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
              >
                <Input
                  placeholder={t(
                    "addBankAccount.accountNumberLabelPlaceholder"
                  )}
                />
              </Form.Item>

              <Form.Item
                label={
                  <RequiredLabel
                    text={t("addBankAccount.confirmAccountNumberLabel")}
                    required={true}
                  />
                }
                name="confirmAccountNumber"
                rules={[
                  {
                    required: true,
                    message: t(
                      "addBankAccount.confirmAccountNumberErrorMessage"
                    ),
                  },
                  {
                    whitespace: true,
                    message: t(
                      "addBankAccount.confirmAccountNumberErrorMessage"
                    ),
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("accountNumber") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        t("addBankAccount.validateAccountNumberErrorMessage")
                      );
                    },
                  }),
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
              >
                <Input
                  placeholder={t(
                    "addBankAccount.confirmAccountNumberPlaceholder"
                  )}
                />
              </Form.Item>

              <Form.Item
                label={
                  <RequiredLabel
                    text={t("addBankAccount.ifcsCodeLabel")}
                    required={true}
                  />
                }
                name="bankCode"
                rules={[
                  {
                    required: true,
                    message: t("addBankAccount.ifcsCodeErrorMessage"),
                  },
                  {
                    whitespace: true,
                    message: t("addBankAccount.ifcsCodeErrorMessage"),
                  },
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
              >
                <Input placeholder={t("addBankAccount.ifcsCodePlaceholder")} />
              </Form.Item>

              <Form.Item
                label={
                  <RequiredLabel
                    text={t("addBankAccount.bankNameLabel")}
                    required={true}
                  />
                }
                name="bankName"
                rules={[
                  {
                    required: true,
                    message: t("addBankAccount.bankNameValidErrorMessage"),
                  },
                  {
                    whitespace: true,
                    message: t("addBankAccount.bankNameValidErrorMessage"),
                  },
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
              >
                <Input placeholder={t("addBankAccount.bankNamePlaceholder")} />
              </Form.Item>

              <Form.Item
                label={
                  <RequiredLabel
                    text={t("addBankAccount.swiftCodeLabel")}
                    required={true}
                  />
                }
                name="swiftCode"
                rules={[
                  {
                    required: true,
                    message: t("addBankAccount.swiftCodeErrorMessage"),
                  },
                  {
                    whitespace: true,
                    message: t("addBankAccount.swiftCodeErrorMessage"),
                  },
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
              >
                <Input placeholder={t("addBankAccount.swiftCodePlaceholder")} />
              </Form.Item>

              <Form.Item
                label={
                  <RequiredLabel
                    text={t("addBankAccount.routingNumber")}
                    required={true}
                  />
                }
                name="routing_number"
                rules={[
                  {
                    required: true,
                    message: t("addBankAccount.pleaseEnterRoutingNumber"),
                  },
                  {
                    whitespace: true,
                    message: t("addBankAccount.pleaseEnterRoutingNumber"),
                  },
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
              >
                <Input placeholder={t("addBankAccount.enterRoutingNumber")} />
              </Form.Item>
            </div>

            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ width: "100%" }}
            >
              {t("addBankAccount.update")}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </>
  );
};

export default AddBankAccount;
